import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Send, Copy, Mail, MessageCircle, Facebook, Twitter } from 'lucide-react';
import { toast } from 'sonner';

interface VideoShareButtonProps {
  videoId: string;
  videoTitle?: string;
}

const VideoShareButton = ({ videoId, videoTitle }: VideoShareButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const shareUrl = `${window.location.origin}/video/${videoId}`;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(videoTitle || 'Check out this video');

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard');
      setIsOpen(false);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const shareOptions = [
    {
      name: 'Copy link',
      icon: Copy,
      action: handleCopyLink,
    },
    {
      name: 'Messages',
      icon: MessageCircle,
      action: () => {
        if (navigator.share) {
          navigator.share({
            title: videoTitle,
            url: shareUrl,
          }).catch(() => {});
        }
        setIsOpen(false);
      },
    },
    {
      name: 'X (Twitter)',
      icon: Twitter,
      action: () => {
        window.open(
          `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
          '_blank'
        );
        setIsOpen(false);
      },
    },
    {
      name: 'Facebook',
      icon: Facebook,
      action: () => {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
          '_blank'
        );
        setIsOpen(false);
      },
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      action: () => {
        window.open(
          `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
          '_blank'
        );
        setIsOpen(false);
      },
    },
    {
      name: 'Email',
      icon: Mail,
      action: () => {
        window.location.href = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;
        setIsOpen(false);
      },
    },
    {
      name: 'Telegram',
      icon: MessageCircle,
      action: () => {
        window.open(
          `https://t.me/share/url?url=${encodedUrl}&text=${encodedTitle}`,
          '_blank'
        );
        setIsOpen(false);
      },
    },
  ];

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2 hover:bg-foreground hover:text-background">
          <Send className="h-4 w-4" />
          Share
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="grid gap-1">
          {shareOptions.map((option) => {
            const Icon = option.icon;
            return (
              <Button
                key={option.name}
                variant="ghost"
                className="w-full justify-start gap-3 h-10"
                onClick={option.action}
              >
                <Icon className="h-4 w-4" />
                {option.name}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default VideoShareButton;
